// This module provides functions to check if entity types support FormulaDisabled interface
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { Lazy } from "common/data/lazy";
import type { InterfaceSchema, Schema } from "common/data/schema";
import { SchemaResolver } from "common/data/schemaValidator";
import { isDocEntityBase } from "doc/entity/docEntityBaseTools";
import { DocSchemaLibrary } from "schemas-common/docSchema";
import { docEntityTypeToSchemaName } from "./docEntityTypeToSchemaName";

const resolver = new Lazy(() => new SchemaResolver(DocSchemaLibrary));

const entityTypeSupportsFormulaDisabledCache = new Map<DocEntityType, boolean>();

function schemaSupportsFormulaDisabled(schema: Schema): boolean {
  const resolvedSchema = resolver.value.resolveSchema(schema);

  if (resolvedSchema.kind === "INTERFACE") {
    return checkInterfaceForDisabledField(resolvedSchema);
  }

  // Handle union types - check if any variant has the disabled field
  if (resolvedSchema.kind === "UNION") {
    return resolvedSchema.unionTypes.some((variant) => {
      const resolvedVariant = resolver.value.resolveSchema(variant);
      return resolvedVariant.kind === "INTERFACE" && checkInterfaceForDisabledField(resolvedVariant);
    });
  }

  return false;
}

function checkInterfaceForDisabledField(interfaceSchema: InterfaceSchema): boolean {
  const disabledField = interfaceSchema.fields["disabled"];

  if (!disabledField) {
    return false;
  }

  // Check if the disabled field references FormulaDisabledField
  if (disabledField.kind === "IDENTIFIER") {
    return disabledField.name === "FormulaDisabledField" || disabledField.name === "ParameterFormulaDisabledField";
  }

  return false;
}

export function entityTypeSupportsFormulaDisabled(entityType: DocEntityType): boolean {
  const cached = entityTypeSupportsFormulaDisabledCache.get(entityType);
  if (cached !== undefined) {
    return cached;
  }

  const schemaName = docEntityTypeToSchemaName[entityType];
  const result = schemaName !== undefined && schemaSupportsFormulaDisabled(DocSchemaLibrary[schemaName]);

  entityTypeSupportsFormulaDisabledCache.set(entityType, result);

  return result;
}

/**
 * Check if an entity instance's type supports FormulaDisabled
 */
export function docEntityHasFormulaDisabledField(entity: any): boolean {
  if (typeof entity !== "object" || entity === null) {
    return false;
  }

  if (!isDocEntityBase(entity)) {
    return false;
  }

  return entityTypeSupportsFormulaDisabled(entity.entityType);
}
